"use client";

import React, { useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
const categories = [{
  title: "Eligibility",
  faqs: [{
    q: "Do I need to be on an HSE waiting list to qualify?",
    a: "Yes. To claim reimbursement under the EU Cross-Border Directive, you must be eligible for the same treatment in the Irish public health system and have a referral from your GP or a public consultant confirming you are on a waiting list. If you are not on a waiting list, you can still travel for treatment but you will not be eligible for HSE reimbursement."
  }, {
    q: "Can I apply if I have private health insurance in Ireland?",
    a: "Yes, having private insurance does not disqualify you from the Cross-Border Directive. However, you must pay for treatment at Clinic Agatas directly yourself — payment by an insurance company is not eligible for HSE reimbursement. You may wish to check with your insurer separately whether they cover any element of your treatment abroad."
  }, {
    q: "Does my GP need to refer me, or can I contact you directly?",
    a: "You can contact us directly at any time. However, if you intend to claim HSE reimbursement, you will need a referral letter from your GP dated before your consultation at Clinic Agatas. We recommend getting this early in the process. Our specialist will advise you on exactly what the letter needs to contain."
  }, {
    q: "Can I bring a family member with me?",
    a: "Yes. Many patients bring a partner or family member for support. Their travel and accommodation costs are not reimbursable under the HSE scheme, but there are no restrictions on accompanying persons. Egle Rehabilitation center can accommodate companions in the same or separate room at the standard rate."
  }, {
    q: "Do I need a GP referral to be treated at Clinic Agatas?",
    a: "No, you do not need a referral to contact us or to be treated privately. A GP or public consultant referral is only required if you want to claim reimbursement from the HSE under the EU Cross-Border Directive, and it must be dated before your consultation."
  }]
}, {
  title: "The Process",
  faqs: [{
    q: "How do I get started?",
    a: "Fill in our enquiry form, send us an email or call us. We will contact you, listen to your situation and assess whether we can help. If surgery is appropriate, we arrange a remote consultation with your neurosurgeon. You send your MRI scans in advance so he can review your case before the call."
  }, {
    q: "How long does the whole process take from first contact to returning home?",
    a: "The timeline depends largely on how quickly you submit your HSE prior notification and receive a response. HSE responds within 6 weeks. Once that is confirmed, surgery can typically be scheduled within a few weeks. You will need approximately 3 to 4 weeks in Lithuania in total — one day pre-operative, one to two nights in the clinic, and 12 to 20 days in Egle Rehabilitation center."
  }, {
    q: "Do I need to speak Lithuanian?",
    a: "No. All three neurosurgeons at Clinic Agatas consult international patients directly in English. All pre-operative briefings, post-operative instructions and discharge documentation are provided in English. Staff at the front desk also speak English."
  }, {
    q: "What happens if I need to cancel or postpone my surgery?",
    a: "Contact us as early as possible. We will reschedule your surgery at no additional charge where possible. If you have already submitted a prior notification to HSE, you may need to update the dates. We will advise you on what steps are needed."
  }, {
    q: "Who arranges my accommodation and transfers in Kaunas?",
    a: "We arrange the transfer from Clinic Agatas to Egle Rehabilitation center after your surgery at no charge. For hotel accommodation before surgery and other transfers, we can provide recommendations and assist with arrangements on request. Flights are booked independently by you."
  }]
}, {
  title: "Surgery and Recovery",
  faqs: [{
    q: "How long will I need to stay in Lithuania?",
    a: "Plan for approximately 3 to 4 weeks in total. You arrive the day before surgery, stay one to two nights at Clinic Agatas after your procedure, then transfer to Egle Rehabilitation center in Birstonas for 12 to 20 days of supervised rehabilitation. Your neurosurgeon confirms you are safe to fly before you leave."
  }, {
    q: "Is it safe to fly after spinal surgery?",
    a: "We recommend waiting 14 to 21 days before flying following spinal surgery, depending on the procedure performed and your recovery progress. Before your departure, your neurosurgeon carries out a final follow-up at Clinic Agatas and confirms you are safe to fly. For longer flights, business class with lie-flat seating is recommended where possible."
  }, {
    q: "What happens if I have a complication after I return home?",
    a: "Follow-up consultations are available online after you return to Ireland. We provide your GP with a full Discharge Summary and Operative Report so they have complete clinical information. If you need further assessment, we can coordinate remotely with your GP. In an emergency, contact your local hospital."
  }, {
    q: "Will my GP in Ireland be informed about my treatment?",
    a: "Yes. After your procedure, we issue a complete Discharge Summary and Operative Report. These are provided in English and contain all clinical information your GP would need, including your diagnosis, procedure details, DRG code and post-operative instructions. This documentation is also required for your HSE reimbursement claim."
  }]
}, {
  title: "HSE Reimbursement",
  faqs: [{
    q: "How much can I claim back from HSE?",
    a: "HSE reimburses the lesser of two amounts: what you paid at Clinic Agatas, or the Irish public rate for the same procedure. For spinal surgery (microdiscectomy, nucleoplasty, vertebroplasty), the maximum HSE day case rate is €8,558. For pain management procedures, the maximum is €874. For outpatient consultations, up to €194 per day. Travel, accommodation and meals are not reimbursable."
  }, {
    q: "How long does HSE take to process my claim?",
    a: "HSE aims to process completed claims within 30 working days of receiving all required documents. Incomplete applications are the most common cause of delays. We provide all clinical documentation from Clinic Agatas in the correct format. You are responsible for your GP referral letter and proof of payment."
  }, {
    q: "Do I need prior authorisation before I travel?",
    a: "Prior authorisation is not mandatory for most procedures, but it is strongly recommended. Submitting a prior notification to HSE before you travel gives you written confirmation of your eligibility and an indication of your reimbursement amount before you commit to anything. HSE responds within 6 weeks. Our specialist helps you prepare and submit this application."
  }, {
    q: "Can rehabilitation at Egle Rehabilitation center be reimbursed?",
    a: "HSE assesses rehabilitation reimbursement individually based on your procedure and clinical condition. In our experience, physiotherapy sessions are most commonly confirmed for reimbursement. Prior authorisation is strongly recommended before you travel — it is the only way to confirm what will be covered in your specific case. We help you submit this application as part of our standard process."
  }, {
    q: "How much will the HSE reimburse, and is the amount guaranteed?",
    a: "Reimbursement is assessed by the HSE and we cannot guarantee the amount you will receive. The figures shown are the HSE day case rates effective 1 July 2025 and are provided as a guide only."
  }]
}];
function Hero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="relative w-full bg-[#264256] overflow-hidden min-h-[40vh] md:min-h-[50vh] flex items-center">
      <div className="container mx-auto px-6 max-w-7xl relative z-10 py-10 md:py-16">
        <div ref={ref} className={cn("flex flex-col items-center text-center gap-4 md:gap-6 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <span className="font-default text-[12px] text-[#65d19d] uppercase tracking-widest font-semibold">Frequently Asked Questions</span>
          <h1 className="font-default font-bold text-4xl md:text-[60px] leading-[1.1] text-white max-w-4xl">
            Your questions, answered.
          </h1>
          <p className="font-default text-white/80 text-base md:text-xl leading-relaxed max-w-2xl">
            Answers to the questions we hear most often from Irish patients considering treatment at Clinic Agatas.
          </p>
        </div>
      </div>
    </section>;
}
function FaqCategory({
  category
}: {
  category: typeof categories[0];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
      <h2 className="font-default font-bold text-2xl text-[#264256] mb-6 pb-3 border-b-2 border-[#65d19d]">{category.title}</h2>
      <div className="flex flex-col gap-3">
        {category.faqs.map((faq, i) => <div key={i} data-index={i} className="bg-white rounded-lg border border-[#f1f3f5] shadow-sm overflow-hidden">
            <Button type="button" variant="ghost" onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between px-6 py-5 text-left gap-4 h-auto whitespace-normal rounded-lg hover:bg-transparent">
              <span className="font-default font-bold text-[#264256] text-base leading-snug">{faq.q}</span>
              <ChevronDown className={cn("w-5 h-5 text-[#65d19d] shrink-0 transition-transform duration-200", openIndex === i ? "rotate-180" : "")} />
            </Button>
            {openIndex === i && <div className="px-6 pb-6 border-t border-[#f1f3f5]">
                <p className="font-default text-muted-foreground text-sm leading-relaxed pt-4">{faq.a}</p>
              </div>}
          </div>)}
      </div>
    </div>;
}
export default function Faq() {
  return <>
      <Hero />
      <section className="bg-background py-12 md:py-20">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="flex flex-col gap-10 md:gap-16">
            {categories.map((cat, i) => <div key={i} data-index={i}>
                <FaqCategory category={cat} />
              </div>)}
          </div>
        </div>
      </section>
      <section className="bg-background py-10 md:py-16">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="bg-[#264256] rounded-2xl px-6 md:px-10 py-10 md:py-14 text-center flex flex-col items-center gap-6 md:gap-8">
            <div className="flex flex-col gap-3 md:gap-4">
              <h2 className="font-default font-bold text-2xl md:text-4xl text-white leading-tight">
                Still have a question?
              </h2>
              <p className="font-default text-white/80 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
                If you did not find the answer you were looking for, contact us directly. We respond within one business day.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild className="rounded-lg bg-[#65d19d] text-[#264256] hover:bg-[#65d19d]/90 h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300 shadow-lg">
                <Link to="#">Contact Us</Link>
              </Button>
              <Button asChild className="rounded-lg bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#264256] h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300">
                <Link to="#">HSE Reimbursement Info</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>;
}